import React from "react";
import { motion } from "framer-motion";
import { Wheat, FlaskConical, Droplets, ArrowUpRight } from "lucide-react";

const IMG_PHARMA =
  "https://static.prod-images.emergentagent.com/jobs/5102b00f-8bc4-4859-8124-35bac0e1cdac/images/fece55ba1cb2342f8399c7d2de9ccdfc271ff63644c069b81867fb2c2513aa91.png";
const IMG_WATER =
  "https://images.pexels.com/photos/33514501/pexels-photo-33514501.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_FOOD =
  "https://images.unsplash.com/photo-1766701753136-7b0cae03c036?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHx0aWclMjB3ZWxkaW5nJTIwY2xvc2UlMjB1cHxlbnwwfHx8fDE3Nzc1NDQ1ODl8MA&ixlib=rb-4.1.0&q=85";

const sectors = [
  {
    tag: "Food production",
    title: "Hygienic lines that run — and run clean.",
    body: "Crevice-free TIG welds and hygienic stainless fabrication for food-grade environments — built for reliability, cleanability, and consistent production.",
    icon: Wheat,
    img: IMG_FOOD,
    stats: [
      { k: "304 / 316L", v: "Stainless grades" },
      { k: "Hygienic finish", v: "Built for cleanability" },
    ],
  },
  {
    tag: "Pharmaceuticals",
    title: "Pharma-grade, built to perform.",
    body: "Hygienic stainless pipework, vessels and skids — fabricated with consistency, traceability, and the standards expected in regulated environments.",
    icon: FlaskConical,
    img: IMG_PHARMA,
    stats: [
      { k: "TIG / Orbital-ready", v: "Welding capability" },
      { k: "Traceability", v: "Material control" },
    ],
  },
  {
    tag: "Water & infrastructure",
    title: "Built for demanding environments.",
    body: "Bespoke fabrication and site work delivered where required — supporting wider industrial and infrastructure projects.",
    icon: Droplets,
    img: IMG_WATER,
    stats: [
      { k: "Site installation", v: "Nationwide" },
      { k: "Bespoke builds", v: "Project-specific" },
    ],
  },
];

export const Sectors = () => {
  return (
    <section
      id="sectors"
      data-testid="sectors-section"
      className="relative bg-[#09090b] py-20 md:py-28 overflow-hidden ambient-depth"
    >
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 md:mb-24"
        >
          <div>
            <p className="overline mb-6">— Sectors we serve</p>
            <h2 className="font-heading text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] max-w-2xl">
              Three industries. One standard.
            </h2>
          </div>
          <p className="max-w-sm text-white/55 font-body text-base leading-relaxed">
            Every sector we serve demands regulated, documented, hygienic
            workmanship. That is the baseline at Fina — not the upgrade.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {sectors.map((s, i) => (
            <motion.article
              key={s.tag}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`sector-card-${i}`}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-[#121214] hover:border-white/25 transition-all duration-500"
            >
              <div className="relative h-64 md:h-72 overflow-hidden">
                <img
                  src={s.img}
                  alt={s.title}
                  className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121214] via-[#121214]/40 to-transparent" />
                <div className="absolute top-5 left-5 inline-flex items-center gap-2 glass rounded-full px-3 py-1.5">
                  <s.icon size={14} className="text-[#FF5722]" />
                  <span className="text-[11px] font-mono tracking-widest uppercase text-white/80">
                    {s.tag}
                  </span>
                </div>
              </div>

              <div className="p-7 md:p-9">
                <h3 className="font-heading text-white text-2xl md:text-[26px] leading-tight tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-4 text-white/55 font-body text-sm leading-relaxed">
                  {s.body}
                </p>

                <div className="mt-7 pt-6 border-t border-white/10 grid grid-cols-2 gap-4">
                  {s.stats.map((st) => (
                    <div key={st.k}>
                      <div className="font-heading text-white text-lg">
                        {st.k}
                      </div>
                      <div className="text-white/40 text-xs mt-1 font-body">
                        {st.v}
                      </div>
                    </div>
                  ))}
                </div>

                <a
                  href="#contact"
                  className="mt-7 inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-[#FF5722] transition"
                >
                  Discuss a project
                  <ArrowUpRight size={14} />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
